import { useState, useEffect, useRef } from 'react'
import { Bell } from 'lucide-react'
import { Sheet } from './UI'
import { requestPermission, subscribeToPush } from '../lib/push'
import { safeStorage } from '../lib/safeStorage'
import { isOverlayBusy } from '../lib/overlayBusy'
import { railBusy } from '../lib/toastRail'
import { currentReceipt } from '../lib/batchReceipt'
import { logEvent } from '../lib/analytics'

// ── NotificationNudge — ask for push at the first value moment ────────────────
// Rebuilt 2026-09-21 as a 17 sheet (Writers' Room voice) and re-mounted in
// NotifLayer. The 16.x version was an inline Home card inside the old
// MessagesDrawer; the v17 Home rebuild deleted that mount and nothing replaced
// it, so for a week the app never asked signed-in users for push at all — 6 of
// 46 new accounts had it on. The retention read that made this urgent: of new
// signed-in users, 4 of 6 with push on came back on day 2; 3 of 40 without it.
//
// Asks ONCE, after the first episode mark (the moment "tell me when the next one
// airs" is a sentence about something real), waits for the Undo bar / Pulse peek
// / any cinematic to finish so it never lands on top of them, and never asks
// again after "Not now" (localStorage) or once permission is decided either way.
// Exported for BatchReceipt, which folds this ask into a cascade's receipt row.
export const PUSH_DISMISS_KEY = 'nextep_push_nudge_dismissed'
const DISMISS_KEY = PUSH_DISMISS_KEY
const DELAY_MS = 1400

export function NotificationNudge({ user }) {
  const [armed, setArmed] = useState(false)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const pulseOpen = useRef(false)
  useEffect(() => {
    const on = (e) => { pulseOpen.current = !!e.detail?.open }
    window.addEventListener('nextep:pulse-sheet', on)
    return () => window.removeEventListener('nextep:pulse-sheet', on)
  }, [])

  useEffect(() => {
    // Guests too (2026-09-23): 27 of the 35 people who joined since 9/8 were guests
    // with no email, so push is the ONLY channel that can bring them back, and 7
    // guests already hold subscriptions (they survive the account upgrade, same
    // user id). Cohort read: 2 of 35 returned on day 2; nobody without a mark did.
    if (!user?.id) return
    if (typeof Notification === 'undefined') return
    if (Notification.permission !== 'default') return
    if (safeStorage.get(DISMISS_KEY) === '1') return
    let t
    const onMark = () => { clearTimeout(t); t = setTimeout(() => setArmed(true), DELAY_MS) }
    // Planning a show for Tonight is the other moment "tell me when it airs" is about
    // something concrete (2026-10-09: the first-mark ask converts 4 of 6 but fires rarely).
    window.addEventListener('nextep:episode_marked', onMark)
    window.addEventListener('nextep:plan-added', onMark)
    return () => { clearTimeout(t); window.removeEventListener('nextep:episode_marked', onMark); window.removeEventListener('nextep:plan-added', onMark) }
  }, [user?.id, user?.is_anonymous])

  // Armed → wait for a quiet screen (nothing on the bottom rail, no cinematic), then rise.
  useEffect(() => {
    if (!armed || open) return
    const tick = () => {
      // A batch receipt may have carried the ask while we waited — stand down.
      if (safeStorage.get(DISMISS_KEY) === '1') { setArmed(false); return }
      if (currentReceipt() || pulseOpen.current) return
      if (!isOverlayBusy() && !window.__nextepModalHold && !railBusy('push-nudge')) { setOpen(true); logEvent('push_nudge_shown') }
    }
    tick()
    const iv = setInterval(tick, 400)
    return () => clearInterval(iv)
  }, [armed, open])

  if (!open) return null

  const dismiss = () => { try { safeStorage.set(DISMISS_KEY, '1') } catch {} logEvent('push_nudge_dismissed'); setOpen(false); setArmed(false) }
  const enable = async () => {
    setBusy(true)
    let res = 'denied'
    try { res = await requestPermission(); if (res === 'granted') await subscribeToPush(user.id) } catch { /* noop */ }
    setBusy(false)
    logEvent('push_nudge_answered', { granted: res === 'granted' })
    try { safeStorage.set(DISMISS_KEY, '1') } catch {}
    setOpen(false); setArmed(false)
  }

  return (
    <Sheet open onClose={dismiss} label="Episode alerts" from="bottom" panelStyle={{ padding: '10px 20px calc(24px + env(safe-area-inset-bottom))', background: 'var(--bg-raised)' }}>
      <div style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 16px' }} />
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)', display: 'flex', alignItems: 'center', gap: 6 }}><Bell size={12} />Alerts</div>
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 500, color: 'var(--text-strong)', lineHeight: 1.15, marginTop: 4 }}>Want a nudge when the next one airs?</div>
      <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>One heads-up the day a new episode of a show you’re watching drops, in your timezone. Nothing else, ever, unless you ask.</div>
      <button onClick={enable} disabled={busy} className="ui-hit cta-glow" style={{ marginTop: 20, width: '100%', height: 46, borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--amber)', color: 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
        {busy ? 'Turning on…' : 'Turn on alerts'}
      </button>
      <button onClick={dismiss} style={{ marginTop: 8, width: '100%', height: 42, borderRadius: 'var(--radius-pill)', border: 'none', background: 'none', color: 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 14, cursor: 'pointer' }}>Not now</button>
    </Sheet>
  )
}
